import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Box, Button, Container, Paper, Stack, Typography } from '@mui/material';
import { CalendarToday } from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { EventAccess } from './InvitationAccess';

const CalendarDownload: React.FC<{ onAccessExpired: () => Promise<void> }> = ({ onAccessExpired }) => {
  const { slug = '' } = useParams<{ slug: string }>();
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [downloaded, setDownloaded] = useState(false);
  const pending = useRef(false);
  const active = useRef(true);
  const request = useRef<AbortController | null>(null);

  useEffect(() => {
    active.current = true;
    return () => { active.current = false; request.current?.abort(); };
  }, []);

  const download = async () => {
    if (pending.current) return;
    pending.current = true;
    setDownloading(true);
    setDownloaded(false);
    setError('');
    request.current = new AbortController();
    try {
      // Fetch only after access is granted and the guest explicitly requests it.
      // Keep errors in this frontend flow instead of navigating to API JSON.
      const { data } = await axios.get<Blob>(`/api/events/${encodeURIComponent(slug)}/calendar.ics`, {
        responseType: 'blob', signal: request.current.signal,
      });
      if (!active.current) return;
      if (!(data instanceof Blob) || !/^text\/calendar(?:;|$)/i.test(data.type)) throw new Error('Invalid calendar response');
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      try {
        link.href = url;
        link.download = `${slug}-calendar.ics`;
        document.body.appendChild(link);
        link.click();
      } finally {
        link.remove();
        // Give the browser time to start consuming the file before releasing it.
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
      setDownloaded(true);
    } catch (error) {
      if (!active.current) return;
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 401 || status === 403) await onAccessExpired();
      else setError('Unable to download the calendar. Please try again.');
    } finally {
      pending.current = false;
      if (active.current) setDownloading(false);
    }
  };

  return <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
    <Container maxWidth="sm" sx={{ py: { xs: 3, md: 6 } }}>
      <Paper sx={{ p: { xs: 3, sm: 4 } }}>
        <Stack spacing={2.5}>
          <CalendarToday color="primary" fontSize="large" />
          <Typography variant="h4" component="h1">Add to your calendar</Typography>
          <Typography color="text.secondary">Download the event's calendar file, then open it in your calendar app.</Typography>
          {error && <Alert severity="error">{error}</Alert>}
          {downloaded && <Alert severity="success">Calendar file ready. Open the downloaded .ics file to add the event.</Alert>}
          <Button variant="contained" fullWidth onClick={download} disabled={downloading}>
            {downloading ? 'Downloading…' : 'Download calendar (.ics)'}
          </Button>
        </Stack>
      </Paper>
    </Container>
  </Box>;
};

const EventCalendar: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { refresh } = useAuth();
  const [attempt, setAttempt] = useState(0);
  const recheckAccess = async () => {
    // Refresh even a cached Forms session: it may have expired since mounting.
    // Remount the gate so revoked guest grants also require a new invitation.
    setAttempt(value => value + 1);
    await refresh();
  };
  return <EventAccess key={`${slug}:${attempt}`} standalone>
    <CalendarDownload onAccessExpired={recheckAccess} />
  </EventAccess>;
};

export default EventCalendar;
